import type * as React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ImagePlus, Maximize2, X } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Label } from '../../../components/ui/label';
import { formatBytes } from '../lib/format-bytes';
import { cn } from '../../../components/ui/utils';
import { hasPlainText, imageFromClipboard, pickPasteTarget } from './image-paste';
import { useObjectUrl } from './use-object-url';

/**
 * Картинки в форме отчёта: превью, просмотр целиком, перетаскивание и Ctrl+V.
 *
 * Отдельным файлом, потому что таких полей в форме сразу несколько —
 * логотип, титул и по скриншоту на каждую статью, — и все они ведут себя
 * одинаково. Заодно здесь живёт разводка вставки: браузер сообщает о
 * Ctrl+V один раз на всё окно, а полей на экране много, и кто-то должен
 * решать, какому из них досталась картинка.
 */

type PasteTarget = {
  id: string;
  /** Имя поля — его называем в уведомлении о вставке. */
  label: string;
  hasFile: boolean;
  disabled: boolean;
  element: HTMLElement | null;
  accept: (file: File) => void;
};

type PasteRegistry = {
  register: (target: PasteTarget) => () => void;
  /** Поле, которого коснулись: следующая вставка достанется ему. */
  arm: (id: string) => void;
};

const PasteContext = createContext<PasteRegistry | null>(null);

const DIALOG = '[data-slot="dialog-content"]';
const EDITABLE = 'input, textarea, [contenteditable="true"]';

function isEditable(node: Node): boolean {
  const element = node instanceof Element ? node : node.parentElement;
  return Boolean(element?.closest(EDITABLE));
}

function byDomOrder(a: { element: HTMLElement }, b: { element: HTMLElement }): number {
  const position = a.element.compareDocumentPosition(b.element);
  if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
  if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
  return 0;
}

/** Ctrl+V с картинкой где угодно на странице попадает в одно из полей внутри. */
export function ImagePasteProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const targets = useRef(new Map<string, PasteTarget>());
  const armed = useRef<string | null>(null);

  const register = useCallback((target: PasteTarget) => {
    targets.current.set(target.id, target);
    return () => {
      targets.current.delete(target.id);
      if (armed.current === target.id) armed.current = null;
    };
  }, []);

  const arm = useCallback((id: string) => {
    armed.current = id;
  }, []);

  useEffect(() => {
    function onPaste(event: ClipboardEvent): void {
      const file = imageFromClipboard(event.clipboardData);
      if (!file) return;
      const node = event.target instanceof Node ? event.target : null;
      const all = [...targets.current.values()];
      // Вставили прямо в поле — оно разберётся само, без нашей разводки.
      if (node && all.some((t) => t.element?.contains(node))) return;
      // В поле ввода текст важнее картинки: туда вставляют ссылки.
      if (node && isEditable(node) && hasPlainText(event.clipboardData)) return;

      const open = all.filter(
        (t): t is PasteTarget & { element: HTMLElement } =>
          !t.disabled && Boolean(t.element?.isConnected),
      );
      open.sort(byDomOrder);
      const id = pickPasteTarget(
        open.map((t) => ({ id: t.id, hasFile: t.hasFile, inDialog: Boolean(t.element.closest(DIALOG)) })),
        armed.current,
      );
      const target = open.find((t) => t.id === id);
      if (!target) return;
      event.preventDefault();
      target.accept(file);
      toast.success(`Картинка вставлена: ${target.label}`);
    }

    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []);

  const registry = useMemo(() => ({ register, arm }), [register, arm]);
  return <PasteContext.Provider value={registry}>{children}</PasteContext.Provider>;
}

/** Картинка, которую можно открыть во весь экран — посмотреть, что загрузили. */
export function ImagePreview({
  src,
  name,
  className,
}: {
  src: string;
  name: string;
  className?: string;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        aria-label={`Посмотреть целиком: ${name}`}
        onClick={() => setOpen(true)}
        className={cn(
          'group/preview relative flex shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
          className,
        )}
      >
        <img src={src} alt="" className="h-full w-full object-contain" />
        <span className="absolute inset-0 flex items-center justify-center bg-background/70 opacity-0 transition-opacity group-hover/preview:opacity-100">
          <Maximize2 className="size-4" />
        </span>
      </button>

      {/*
        Окно под картинку, а не под текст: поля маленькие, место отдано
        изображению. object-contain, а не cover: обрезать скриншот ради
        ровной рамки — значит спрятать то, ради чего его и открыли.
      */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="gap-3 sm:max-w-3xl">
          <DialogTitle className="pr-8 text-sm leading-snug font-medium">{name}</DialogTitle>
          <DialogDescription className="sr-only">Просмотр выбранной картинки</DialogDescription>
          <img src={src} alt={name} className="max-h-[70svh] w-full rounded-md object-contain" />
        </DialogContent>
      </Dialog>
    </>
  );
}

type ImageFieldProps = {
  id: string;
  label: string;
  /** Подсказка, пока картинки нет. */
  hint: string;
  /**
   * Blob, а не File: сюда попадает и выбранный человеком файл, и картинка,
   * которую нарисовали мы сами (экран статистики) — у неё имени нет.
   */
  file: Blob | null;
  onChange: (file: File | null) => void;
  disabled: boolean;
  /** Имя для картинки без имени — своё, а не «blob». */
  fallbackName?: string;
  /** Ограничение размера, если оно есть на той стороне (логотипы — 5 МБ). */
  maxBytes?: number;
  /**
   * Значок пустого поля. По умолчанию общий «добавить картинку», но когда
   * рядом стоят два поля с разной судьбой — скриншот на разбор и картинка в
   * макет, — одинаковые квадраты путают, и значок это единственное, что видно
   * раньше подписи.
   */
  icon?: React.ComponentType<{ className?: string }>;
};

/** Поле картинки: выбрать файл, перетащить или вставить из буфера. */
export function ImageField({
  id,
  label,
  hint,
  file,
  onChange,
  disabled,
  fallbackName,
  maxBytes,
  icon: Icon = ImagePlus,
}: ImageFieldProps): React.ReactElement {
  const preview = useObjectUrl(file);
  const inputRef = useRef<HTMLInputElement>(null);
  const zoneRef = useRef<HTMLDivElement>(null);
  const [isOver, setIsOver] = useState(false);
  const registry = useContext(PasteContext);

  const accept = useCallback(
    (next: File): void => {
      if (!next.type.startsWith('image/')) {
        toast.error('Нужна картинка');
        return;
      }
      if (maxBytes && next.size > maxBytes) {
        toast.error(`Файл больше ${formatBytes(maxBytes)}`);
        return;
      }
      onChange(next);
    },
    [maxBytes, onChange],
  );

  useEffect(() => {
    if (!registry) return;
    return registry.register({
      id,
      label,
      hasFile: file !== null,
      disabled,
      element: zoneRef.current,
      accept,
    });
  }, [registry, id, label, file, disabled, accept]);

  const name = file instanceof File ? file.name : (fallbackName ?? 'Картинка');

  return (
    <div className="space-y-2">
      {label && <Label htmlFor={id}>{label}</Label>}
      <div
        ref={zoneRef}
        tabIndex={disabled ? -1 : 0}
        aria-label={`${label || 'Картинка'}: перетащите файл или вставьте картинку`}
        data-over={isOver || undefined}
        onFocus={() => registry?.arm(id)}
        onPointerDown={() => registry?.arm(id)}
        onDragOver={(e) => {
          if (disabled) return;
          e.preventDefault();
          setIsOver(true);
        }}
        onDragLeave={() => setIsOver(false)}
        onDrop={(e) => {
          if (disabled) return;
          e.preventDefault();
          setIsOver(false);
          const dropped = imageFromClipboard(e.dataTransfer);
          if (dropped) accept(dropped);
        }}
        onPaste={(e) => {
          if (disabled) return;
          const pasted = imageFromClipboard(e.clipboardData);
          if (!pasted) return;
          e.preventDefault();
          accept(pasted);
        }}
        className="group flex items-center gap-3 rounded-lg border border-dashed p-2 outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 data-over:border-ring data-over:bg-accent/50"
      >
        {preview ? (
          <ImagePreview src={preview} name={name} className="size-16" />
        ) : (
          <div className="flex size-16 shrink-0 items-center justify-center rounded-md border bg-muted">
            <Icon className="size-5 text-muted-foreground" />
          </div>
        )}

        {/*
          Две строки и не больше: полей с картинками в форме много, и
          каждое лишнее предложение множится на их число. Про
          перетаскивание и Ctrl+V говорят пунктирная рамка и aria-label.
        */}
        <div className="min-w-0 flex-1 space-y-1">
          <p className="truncate text-sm">{file ? name : 'Файл не выбран'}</p>
          <p className="text-muted-foreground truncate text-xs">
            {file ? formatBytes(file.size) : hint}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
          >
            {file ? 'Заменить' : 'Выбрать'}
          </Button>
          {file && (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Убрать картинку"
              disabled={disabled}
              onClick={() => onChange(null)}
            >
              <X />
            </Button>
          )}
        </div>
      </div>

      <input
        ref={inputRef}
        id={id}
        type="file"
        accept="image/*"
        className="hidden"
        disabled={disabled}
        onChange={(e) => {
          const next = e.target.files?.[0];
          if (next) accept(next);
          // Иначе повторный выбор того же файла не даст события.
          e.target.value = '';
        }}
      />
    </div>
  );
}
